<script setup>
import { ref, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useKeyboardNavigableList } from 'dashboard/composables/useKeyboardNavigableList';
import SearchAPI from 'dashboard/api/search';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  searchKey: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['selectConversation']);

const { t } = useI18n();
const tagConversationsRef = ref(null);
const selectedIndex = ref(0);
const items = ref([]);
const isFetching = ref(false);

let debounceTimer = null;

async function fetchConversations(q) {
  isFetching.value = true;
  try {
    const { data } = await SearchAPI.conversations({ q: q || '' });
    // Response: { payload: { conversations: [{ id: displayId, contact: { name, thumbnail }, inbox: { name } }] } }
    const records = data?.payload?.conversations || [];
    items.value = records.slice(0, 8).map(c => ({
      id: String(c.id),  // display_id
      contactName: c.contact?.name || t('INTERNAL_CHAT.CONVERSATION_MENTION.UNKNOWN_CONTACT'),
      contactThumbnail: c.contact?.thumbnail || '',
      inboxName: c.inbox?.name || '',
    }));
  } catch {
    items.value = [];
  } finally {
    isFetching.value = false;
  }
}

watch(
  () => props.searchKey,
  newKey => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => fetchConversations(newKey), 250);
  },
  { immediate: true }
);

watch(items, newItems => {
  if (newItems.length < selectedIndex.value + 1) {
    selectedIndex.value = 0;
  }
});

const adjustScroll = () => {
  nextTick(() => {
    if (tagConversationsRef.value) {
      const el = tagConversationsRef.value.querySelector(
        `#conv-mention-item-${selectedIndex.value}`
      );
      if (el) el.scrollIntoView({ block: 'nearest', behavior: 'auto' });
    }
  });
};

const onSelect = () => {
  const item = items.value[selectedIndex.value];
  if (item) emit('selectConversation', item);
};

useKeyboardNavigableList({ items, onSelect, adjustScroll, selectedIndex });

const onHover = index => { selectedIndex.value = index; };
const onItemSelect = index => { selectedIndex.value = index; onSelect(); };
</script>

<template>
  <div>
    <ul
      v-if="isFetching || items.length > 0"
      ref="tagConversationsRef"
      class="vertical dropdown menu mention--box bg-n-solid-1 p-1 rounded-xl text-sm overflow-auto absolute w-full z-20 shadow-md left-0 leading-[1.2] bottom-full max-h-[14rem] border border-solid border-n-strong"
      role="listbox"
    >
      <li v-if="isFetching && !items.length" class="px-3 py-2 text-xs text-n-slate-10">
        {{ t('INTERNAL_CHAT.CONVERSATION_MENTION.SEARCHING') }}
      </li>
      <li
        v-for="(item, index) in items"
        :id="`conv-mention-item-${index}`"
        :key="item.id"
      >
        <div
          :class="{ 'bg-n-alpha-black2': index === selectedIndex }"
          class="flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer"
          role="option"
          @click="onItemSelect(index)"
          @mouseover="onHover(index)"
        >
          <Avatar
            :src="item.contactThumbnail"
            :name="item.contactName"
            :size="24"
            rounded-full
          />
          <div class="min-w-0 flex-1">
            <div class="flex items-center gap-1.5">
              <span class="font-medium text-n-slate-12 truncate">{{ item.contactName }}</span>
              <span class="flex-shrink-0 text-xs text-n-slate-10">#{{ item.id }}</span>
            </div>
            <div v-if="item.inboxName" class="text-xs text-n-slate-10 truncate">
              {{ item.inboxName }}
            </div>
          </div>
        </div>
      </li>
      <li v-if="!isFetching && !items.length" class="px-3 py-2 text-xs text-n-slate-10">
        <Icon icon="i-lucide-search-x" class="inline size-3 mr-1" />
        {{ t('INTERNAL_CHAT.CONVERSATION_MENTION.NO_RESULTS') }}
      </li>
    </ul>
  </div>
</template>
